import { useCallback, useEffect, useState } from 'react';

import type { ToastId } from '#/types/ToastData';
import {
  getLocalStorageJsonItem,
  removeLocalStorageItem,
  setLocalStorageJsonItem,
} from '#/utils/localStorage';

type ToastDismissalData = Record<
  ToastId,
  {
    dismissedAt: number;
  }
>;

const emptyToastDismissalData: ToastDismissalData = {};

export function useToastPersistence(namespace: string) {
  const LS_KEY = `idealist:action-toast-persistence:${namespace}`;

  const getDismissedToasts = useCallback(() => {
    return (
      getLocalStorageJsonItem<ToastDismissalData>(LS_KEY) ??
      emptyToastDismissalData
    );
  }, [LS_KEY]);

  const [dismissedToasts, setDismissedToasts] =
    useState<ToastDismissalData>(getDismissedToasts);

  const clear = useCallback(() => {
    removeLocalStorageItem(LS_KEY);
    setDismissedToasts(emptyToastDismissalData);
  }, [LS_KEY]);

  const persistDismissedToastId = useCallback(
    (toastId: ToastId) => {
      const updatedToasts = {
        ...dismissedToasts,
        [toastId]: {
          dismissedAt: Date.now(),
        },
      };
      setDismissedToasts(updatedToasts);
      setLocalStorageJsonItem(LS_KEY, updatedToasts);
    },
    [dismissedToasts, LS_KEY],
  );

  // Export debug API for testing purposes
  useEffect(() => {
    Object.assign(window, {
      Idealist: {
        ...window.Idealist,
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        debugActionToastPersistence: {
          ...// oxlint-disable-next-line typescript/no-explicit-any
          (window.Idealist as any)
            // oxlint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ?.debugActionToastPersistence,
          [namespace]: {
            dismissedToasts,
            persistDismissedToastId,
            getDismissedToasts,
            clear,
          },
        },
      },
    });
  }, [
    clear,
    dismissedToasts,
    persistDismissedToastId,
    namespace,
    getDismissedToasts,
  ]);

  const hasDismissedToast = useCallback(
    (toastId: ToastId) => toastId in dismissedToasts,
    [dismissedToasts],
  );

  const getDismissal = useCallback(
    (toastId: ToastId) => dismissedToasts[toastId],
    [dismissedToasts],
  );

  const clearExpired = useCallback(
    (maxAgeMs: number, toastId: ToastId) => {
      const dismissal = getDismissal(toastId);
      if (!dismissal) return;

      const age = Date.now() - dismissal.dismissedAt;
      if (age > maxAgeMs) clear();
    },
    [clear, getDismissal],
  );

  return {
    hasDismissedToast,
    getDismissal,
    clear,
    persistDismissedToastId,
    clearExpired,
  };
}
